import setup from '../assets/data/setup.json';
import { embedAdaptabilityQuote, sanitizeHiringManager } from './letterUtils';
import { matchProfileToJob } from './matchProfile';
import { resolveOpenAiKey } from './openAiKey';
import { parseJobDescription } from './parseJobDescription';
import type {
  GenerateOptions,
  GeneratedLetter,
  LetterFormat,
  LetterTone,
  JobDetails,
} from './types';

const { profile, stats } = setup;
const about = setup.about as { story?: string };

function hiringFirstName(job: JobDetails): string {
  const manager = sanitizeHiringManager(job.hiringManager);
  return manager ? ` ${manager.split(' ')[0]}` : '';
}

function greeting(job: JobDetails): string {
  const manager = sanitizeHiringManager(job.hiringManager);
  if (manager) {
    return `Dear ${manager},`;
  }
  if (job.company?.trim()) {
    return `Dear Hiring Team at ${job.company.trim()},`;
  }
  return 'Dear Hiring Team,';
}

function opening(job: JobDetails, tone: LetterTone): string {
  const company = job.company || 'your team';
  const role = job.role || 'this role';

  const openings: Record<LetterTone, string[]> = {
    professional: [
      `I'm writing to express my interest in the ${role} position at ${company}. With ${stats.yearsExperience}+ years building production enterprise software, I believe my background aligns closely with what you're looking for.`,
      `When I read about the ${role} opening at ${company}, it resonated with the kind of work I've been doing for the past several years—shipping reliable systems that solve real business problems.`,
    ],
    warm: [
      `I came across the ${role} role at ${company} and wanted to reach out personally. The problems you're solving are very much in line with work I've done across ERP platforms, SaaS products, and cloud integrations.`,
      `I'd love to be considered for the ${role} position at ${company}. Your focus on building practical, production-grade software is exactly the environment where I do my best work.`,
    ],
    concise: [
      `I'm interested in the ${role} role at ${company}. I have ${stats.yearsExperience}+ years of full-stack experience with enterprise SaaS, integrations, and cloud deployments.`,
    ],
  };

  return pick(openings[tone]);
}

function skillsParagraph(match: ReturnType<typeof matchProfileToJob>, tone: LetterTone): string {
  const skills = match.matchedSkills.slice(0, 5);
  const skillSet = new Set(skills.map((s) => s.toLowerCase()));
  const integrations = match.matchedIntegrations.filter(
    (name) => !skillSet.has(name.toLowerCase()) && !skills.some((s) => s.toLowerCase().includes(name.toLowerCase()))
  );

  if (skills.length === 0 && integrations.length === 0) {
    return match.experienceHighlight;
  }

  const skillPart =
    skills.length > 0
      ? `Technically, I've worked extensively with ${skills.slice(0, -1).join(', ')}${skills.length > 1 ? ', and ' : ''}${skills[skills.length - 1]}.`
      : '';

  const integrationPart =
    integrations.length > 0
      ? `I've also integrated ${integrations.join(', ')} in production.`
      : '';

  if (tone === 'concise') {
    return [match.experienceHighlight, skillPart, integrationPart].filter(Boolean).join(' ');
  }

  const detail = [skillPart, integrationPart].filter(Boolean).join(' ');
  return detail ? `${match.experienceHighlight} ${detail}`.replace(/\s+/g, ' ').trim() : match.experienceHighlight;
}

function impactParagraph(match: ReturnType<typeof matchProfileToJob>): string {
  const impact = match.matchedImpact;

  if (impact) {
    const normalized = impact.replace(/\.$/, '');
    return `${match.projectHighlight} One measurable outcome: ${normalized.charAt(0).toLowerCase()}${normalized.slice(1)}.`;
  }
  return match.projectHighlight;
}

function adaptabilityParagraph(): string {
  const quote = (setup as { quicklyLearnedQuote?: string }).quicklyLearnedQuote;
  if (quote) {
    return embedAdaptabilityQuote(quote);
  }
  return `I'm known for learning new technologies quickly and becoming productive in production codebases—not through shortcuts, but by understanding systems and delivering incrementally.`;
}

function closingParagraph(job: JobDetails, tone: LetterTone): string {
  const company = job.company || 'your organization';

  const closings: Record<LetterTone, string[]> = {
    professional: [
      `I'd welcome the opportunity to discuss how my experience building enterprise platforms and integrations could support ${company}'s goals. Thank you for your time and consideration.`,
      `I would appreciate the chance to speak further about how I can contribute to ${company}. My portfolio and resume are available at ${profile.siteUrl}.`,
    ],
    warm: [
      `I'd genuinely enjoy talking about how I could help ${company} move faster on the problems outlined in this role. Happy to share more context on any of the systems I've built.`,
      `If this sounds like a fit, I'd love to connect. You can also see detailed case studies of my work at ${profile.siteUrl}.`,
    ],
    concise: [
      `Happy to discuss further. Portfolio: ${profile.siteUrl}`,
    ],
  };

  return pick(closings[tone]);
}

function signatureBlock(): string {
  return `${profile.name}\n${profile.title}\n${profile.email} | ${profile.linkedin}`;
}

function applicationOpening(job: JobDetails, tone: LetterTone): string {
  const company = job.company || 'your organization';
  const role = job.role || 'this position';

  const openings: Record<LetterTone, string[]> = {
    professional: [
      `I'm applying for the ${role} role at ${company}. Over the past ${stats.yearsExperience}+ years, I've built and shipped production enterprise software—ERP platforms, SaaS products, and cloud integrations—and this opening aligns well with that work.`,
      `I'm interested in the ${role} opportunity at ${company}. My background is in full-stack engineering for US-based companies, with a focus on reliable systems, API integrations, and software that holds up in production.`,
    ],
    warm: [
      `I'd like to be considered for the ${role} role at ${company}. The problems described in this posting match the kind of work I've been doing—building practical software that helps teams move faster without sacrificing quality.`,
      `I'm applying for the ${role} position at ${company}. I enjoy working on products where engineering decisions directly affect how teams operate day to day, and this role looks like that kind of opportunity.`,
    ],
    concise: [
      `I'm applying for the ${role} role at ${company}. I have ${stats.yearsExperience}+ years of full-stack experience across enterprise SaaS, ERP systems, and cloud deployments.`,
    ],
  };

  return pick(openings[tone]);
}

function applicationClosing(job: JobDetails, tone: LetterTone): string {
  const company = job.company || 'your team';

  const closings: Record<LetterTone, string[]> = {
    professional: [
      `Thank you for reviewing my application. I'd welcome the chance to discuss how my experience could support ${company}.`,
      `I appreciate your consideration and would be glad to share more detail on relevant projects during a conversation.`,
    ],
    warm: [
      `Thanks for taking the time to review my application—I'd love to talk more about how I could contribute to ${company}.`,
      `Happy to walk through any of my work in more detail if helpful. Thank you for your consideration.`,
    ],
    concise: [
      `Thank you for your consideration.`,
    ],
  };

  return pick(closings[tone]);
}

function buildApplicationForm(job: JobDetails, tone: LetterTone): GeneratedLetter {
  const parsed = parseJobDescription(job.jobDescription);
  const match = matchProfileToJob(parsed, job);

  const paragraphs = [
    applicationOpening(job, tone),
    skillsParagraph(match, tone),
    tone !== 'concise' ? impactParagraph(match) : null,
    tone === 'professional' ? adaptabilityParagraph() : null,
    applicationClosing(job, tone),
  ].filter(Boolean) as string[];

  const body = paragraphs.join('\n\n');

  return {
    format: 'application_form',
    greeting: '',
    body,
    closing: '',
    signature: '',
    fullText: body,
  };
}

function buildCoverLetter(job: JobDetails, tone: LetterTone): GeneratedLetter {
  const parsed = parseJobDescription(job.jobDescription);
  const match = matchProfileToJob(parsed, job);

  const paragraphs = [
    opening(job, tone),
    skillsParagraph(match, tone),
    tone !== 'concise' ? impactParagraph(match) : null,
    tone === 'professional' ? adaptabilityParagraph() : null,
    closingParagraph(job, tone),
  ].filter(Boolean) as string[];

  const body = paragraphs.join('\n\n');
  const greet = greeting(job);
  const close = tone === 'concise' ? 'Best regards,' : 'Kind regards,';
  const sig = signatureBlock();

  return {
    format: 'cover_letter',
    subject: `Application — ${job.role}${job.company ? ` at ${job.company}` : ''}`,
    greeting: greet,
    body,
    closing: close,
    signature: sig,
    fullText: `${greet}\n\n${body}\n\n${close}\n\n${profile.name}\n${profile.title}\n${profile.email}`,
  };
}

function buildOutreachEmail(job: JobDetails, tone: LetterTone): GeneratedLetter {
  const parsed = parseJobDescription(job.jobDescription);
  const match = matchProfileToJob(parsed, job);
  const company = job.company || 'your team';

  const body =
    tone === 'concise'
      ? `Hi${hiringFirstName(job)},\n\nI'm a full-stack engineer (${stats.yearsExperience}+ yrs) with enterprise SaaS, ERP, and integration experience. ${match.experienceHighlight}\n\nWould you be open to a brief conversation about the ${job.role || 'opening'}?\n\nBest,\n${profile.name}`
      : `Hi${hiringFirstName(job)},\n\nI noticed the ${job.role || 'engineering'} opportunity at ${company} and wanted to introduce myself briefly.\n\n${match.experienceHighlight} ${match.matchedSkills.length ? `Technically, I've worked extensively with ${match.matchedSkills.slice(0, 4).join(', ')}.` : ''}\n\nIf you're reviewing candidates, I'd appreciate the chance to connect. My portfolio (${profile.siteUrl}) includes case studies on ERP systems, SaaS platforms, and production integrations.\n\nBest regards,\n${profile.name}\n${profile.email}`;

  return {
    format: 'outreach_email',
    subject: `${job.role || 'Engineering role'} — ${profile.name}`,
    greeting: '',
    body,
    closing: '',
    signature: `${profile.name}\n${profile.email}`,
    fullText: `Subject: ${job.role || 'Engineering role'} — ${profile.name}\n\n${body}`,
  };
}

function buildLinkedInMessage(job: JobDetails): GeneratedLetter {
  const parsed = parseJobDescription(job.jobDescription);
  const match = matchProfileToJob(parsed, job);

  const body = `Hi${hiringFirstName(job)} — I saw the ${job.role || 'engineering'} role at ${job.company || 'your company'} and wanted to connect. I'm a full-stack engineer focused on enterprise SaaS and integrations (${stats.yearsExperience}+ yrs). ${match.matchedSkills.slice(0, 3).join(', ')} are areas I've shipped in production. Would love to learn more about the team.`;

  return {
    format: 'linkedin_message',
    greeting: '',
    body,
    closing: '',
    signature: profile.name,
    fullText: body,
  };
}

function buildFollowUp(job: JobDetails): GeneratedLetter {
  const body = `Hi${hiringFirstName(job)},\n\nI wanted to follow up on my application for the ${job.role || 'position'}${job.company ? ` at ${job.company}` : ''}. I remain very interested in the role and believe my experience with enterprise ERP systems, SaaS platforms, and cloud integrations would be a strong fit.\n\nHappy to provide any additional context or walk through relevant projects from my portfolio (${profile.siteUrl}).\n\nThank you again for your consideration.\n\nBest,\n${profile.name}`;

  return {
    format: 'follow_up',
    subject: `Following up — ${job.role}${job.company ? ` at ${job.company}` : ''}`,
    greeting: '',
    body,
    closing: '',
    signature: `${profile.name}\n${profile.email}`,
    fullText: `Subject: Following up — ${job.role}${job.company ? ` at ${job.company}` : ''}\n\n${body}`,
  };
}

function buildRecruiterIntro(job: JobDetails): GeneratedLetter {
  const parsed = parseJobDescription(job.jobDescription);
  const match = matchProfileToJob(parsed, job);

  const body = `Hi,\n\nI'm ${profile.name}, a ${profile.title} based remotely with ${stats.yearsExperience}+ years of experience. I'm exploring ${job.role || 'senior full-stack'} opportunities${job.company ? ` including roles like the one at ${job.company}` : ''}.\n\nQuick snapshot:\n• ${about.story?.split('.')[0] || 'Enterprise full-stack engineer'}.\n• ${match.experienceHighlight}\n• Stack: ${match.matchedSkills.slice(0, 5).join(', ') || 'React, Node.js, TypeScript, PHP, AWS'}.\n\nOpen to contract or full-time. Resume and portfolio: ${profile.siteUrl}\n\nBest,\n${profile.name}\n${profile.email}`;

  return {
    format: 'recruiter_intro',
    subject: `${profile.title} — ${stats.yearsExperience}+ yrs | Open to opportunities`,
    greeting: '',
    body,
    closing: '',
    signature: `${profile.name}\n${profile.email}`,
    fullText: `Subject: ${profile.title} — ${stats.yearsExperience}+ yrs | Open to opportunities\n\n${body}`,
  };
}

function pick<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

const builders: Record<LetterFormat, (job: JobDetails, tone: LetterTone) => GeneratedLetter> = {
  cover_letter: buildCoverLetter,
  application_form: buildApplicationForm,
  outreach_email: buildOutreachEmail,
  linkedin_message: (job) => buildLinkedInMessage(job),
  follow_up: (job) => buildFollowUp(job),
  recruiter_intro: (job) => buildRecruiterIntro(job),
};

export function generateLetter(options: GenerateOptions): GeneratedLetter {
  return builders[options.format](options.job, options.tone);
}

export async function generateLetterWithAi(options: GenerateOptions): Promise<GeneratedLetter> {
  const key = resolveOpenAiKey(options.openAiKey);
  if (!key) {
    return generateLetter(options);
  }

  const parsed = parseJobDescription(options.job.jobDescription);
  const match = matchProfileToJob(parsed, options.job);

  const formatInstructions: Record<LetterFormat, string> = {
    cover_letter: 'Write a professional cover letter (3-4 paragraphs). Sound human, not generic. No buzzword stuffing.',
    application_form:
      'Write text for a job application portal cover letter field (180-320 words). Plain paragraphs only—no subject line, no "Dear...", no name/email signature (those are separate form fields). Mention portfolio only if natural. End with a brief thank-you, not a formal sign-off.',
    outreach_email: 'Write a short, professional outreach email (under 180 words). Direct and respectful.',
    linkedin_message: 'Write a LinkedIn connection message (under 300 characters if possible, max 500).',
    follow_up: 'Write a polite follow-up email after applying (under 150 words).',
    recruiter_intro: 'Write a recruiter introduction email with bullet highlights (under 200 words).',
  };

  const systemPrompt = `You are helping ${profile.name}, a ${profile.title}, write job application materials. Use first person. Be specific, warm, and professional—never robotic. Do not use phrases like "I am excited to apply" or "perfect fit". Use concrete details from the candidate profile only.`;

  const userPrompt = `Format: ${options.format}
Tone: ${options.tone}
${formatInstructions[options.format]}

Company: ${options.job.company || 'Unknown'}
Role: ${options.job.role || 'Unknown'}
Hiring manager: ${options.job.hiringManager || 'Unknown'}

Job description:
${options.job.jobDescription.slice(0, 4000)}

Candidate context:
- ${about.story || profile.heroOneLiner}
- Experience highlight: ${match.experienceHighlight}
- Project highlight: ${match.projectHighlight}
- Relevant skills: ${match.matchedSkills.join(', ')}
- Integrations: ${match.matchedIntegrations.join(', ')}
- Years: ${stats.yearsExperience}+
- Portfolio: ${profile.siteUrl}
- Email: ${profile.email}

Return ONLY the letter body text (include greeting and sign-off). For emails include "Subject: ..." on the first line. For application_form, return plain paragraphs only with no subject or signature block.`;

  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${key}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        temperature: 0.75,
        max_tokens: 900,
      }),
    });

    if (!response.ok) throw new Error('AI request failed');

    const data = await response.json();
    const text = data.choices?.[0]?.message?.content?.trim() || '';

    if (!text) return generateLetter(options);

    const subjectMatch = text.match(/^Subject:\s*(.+)$/m);
    const subject = subjectMatch?.[1];

    return {
      format: options.format,
      subject,
      greeting: '',
      body: text.replace(/^Subject:.+\n?/m, '').trim(),
      closing: '',
      signature: options.format === 'application_form' ? '' : `${profile.name}\n${profile.email}`,
      fullText:
        options.format === 'application_form'
          ? text.replace(/^Subject:.+\n?/m, '').trim()
          : subject
            ? `Subject: ${subject}\n\n${text.replace(/^Subject:.+\n?/m, '').trim()}`
            : text,
    };
  } catch {
    return generateLetter(options);
  }
}
